import { Sparkles, Crown } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface UsageIndicatorProps {
  plan: 'free' | 'premium';
  monthlyUsage: number;
  plantCount: number;
  maxAiUsage: number;
  maxPlants: number;
  onUpgradeClick?: () => void;
  isLoading?: boolean;
}

export function UsageIndicator({
  plan,
  monthlyUsage,
  plantCount,
  maxAiUsage,
  maxPlants,
  onUpgradeClick,
  isLoading = false,
}: UsageIndicatorProps) {
  if (isLoading) {
    return (
      <Card className="border-border">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-7 w-20" />
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-8" />
              </div>
              <Skeleton className="h-2 w-full" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-8" />
              </div>
              <Skeleton className="h-2 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (plan === 'premium') {
    return (
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            <span className="font-semibold text-amber-700 dark:text-amber-400">Premium</span>
          </div>
          <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
            Uso ilimitado
          </p>
        </CardContent>
      </Card>
    );
  }

  const aiUsagePercent = Math.min((monthlyUsage / maxAiUsage) * 100, 100);
  const plantPercent = Math.min((plantCount / maxPlants) * 100, 100);

  return (
    <Card className="border-border">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Seu uso este mês</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onUpgradeClick}
            className="text-xs h-7"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Premium
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Identificações/Diagnósticos</span>
              <span className={monthlyUsage >= maxAiUsage ? 'text-destructive font-medium' : ''}>
                {monthlyUsage}/{maxAiUsage}
              </span>
            </div>
            <Progress 
              value={aiUsagePercent} 
              className={`h-2 ${monthlyUsage >= maxAiUsage ? '[&>div]:bg-destructive' : ''}`}
            />
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Plantas cadastradas</span>
              <span className={plantCount >= maxPlants ? 'text-destructive font-medium' : ''}>
                {plantCount}/{maxPlants}
              </span>
            </div>
            <Progress 
              value={plantPercent} 
              className={`h-2 ${plantCount >= maxPlants ? '[&>div]:bg-destructive' : ''}`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
